import type { FlowStep, IntakeField } from '@fitting-room/contracts'
import { isAddressField, isNameField } from '@fitting-room/contracts/contact'

/** One intake field, and the step that owns it: `profile` is addressed by step. */
export interface ContactField {
  readonly stepId: string
  readonly field: IntakeField
}

/** Where a name and an address given outside the questionnaire are written. */
export interface ContactFields {
  readonly name: ContactField | null
  readonly address: ContactField | null
}

/**
 * The flow's own contact fields, found without naming one.
 *
 * The client must not know a field is called `email` or `name` — those are rows
 * of tenant data, and a boutique that writes `contact_email` or `full_name`
 * must not silently lose the control that writes to it. WHAT makes a field one
 * or the other is `@fitting-room/contracts/contact`, in the contract, because
 * the staff console reads these same values back out of her profile and two
 * answers to "which field is the address" is a form writing to one field and a
 * list reading another.
 *
 * What is here is only the traversal: first match wins, a flow may
 * legitimately have neither, and each is carried with the step that owns it
 * because `profile` is addressed by step. Both callers — R10's panel and the
 * guest's last step — read this one function.
 */
export function contactFields(steps: readonly FlowStep[]): ContactFields {
  let name: ContactField | null = null
  let address: ContactField | null = null

  for (const step of steps) {
    if (step.kind !== 'intake') continue
    for (const field of step.fields) {
      if (!name && isNameField(field)) name = { stepId: step.id, field }
      if (!address && isAddressField(field)) address = { stepId: step.id, field }
    }
    if (name && address) break
  }

  return { name, address }
}

/** What that field already holds: her own answer first, then the booking. */
export function openingValue(
  target: ContactField | null,
  profile: Readonly<Record<string, string | number>>,
  prefill: Readonly<Record<string, string>>,
): string {
  if (!target) return ''
  const stored = profile[target.field.key]
  if (stored !== undefined) return String(stored)
  return prefill[target.field.key] ?? ''
}

/** Nothing to send for a value the session already holds exactly. */
export function isUnchanged(
  target: ContactField,
  raw: string,
  profile: Readonly<Record<string, string | number>>,
): boolean {
  const stored = profile[target.field.key]
  return stored !== undefined && String(stored) === raw
}
